import { useCallback, useEffect, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { useForm } from 'react-hook-form';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useAutoBroadcastSellerAccountSettings } from 'src/hooks/api/useAutoBroadcastSellerAccountSettings';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { tryAutoBroadcastListing } from 'src/utils/autoBroadcastUtils';
import { EMPTY_GUID } from 'src/utils/constants/constants';
import { isSuccess } from 'src/utils/errorUtils';
import { compareMarketplace } from 'src/utils/eventWithDataUtils';
import { getAllInPriceFromListPriceForListing } from 'src/utils/inventoryUtils';
import { roundToPrecision } from 'src/utils/numberFormatter';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Feature,
  ListingClient,
  ListingDetailDataField,
  ListingDetails,
  ListingDetailsPricingSectionUpdates,
  ListingMarketplacePriceUpdateInput,
  Marketplace,
} from 'src/WebApiController';

const getListingDetailPricingInput = (
  listing: ListingDetails | null | undefined
): ListingDetailsPricingSectionUpdates => {
  const marketplacePriceUpdates = listing?.mkpListings
    ? listing.mkpListings
        ?.filter((m) => m.mkp !== Marketplace.StubHub)
        ?.toSorted((a, b) => compareMarketplace(a.mkp, b.mkp))
        ?.map((x) => {
          const listPriceMarkup = x.markup ?? x.sellerAccountDefaultMarkup ?? 0;
          const listingMarketplacePrice = x.netProcs ?? listing?.listPrice;

          return {
            allInPrice: x.isPriceByMkp
              ? (x.webPrice ?? listing?.allInPrice)
              : null,
            listPrice: x.isPriceByMkp
              ? listingMarketplacePrice
              : (listingMarketplacePrice ?? 0) * (1 + listPriceMarkup),
            priceByMarketplace: x.isPriceByMkp,
            marketplace: x.mkp,
            markup:
              x.markup != null ? roundToPrecision(x.markup * 100, 2) : null,
          } as ListingMarketplacePriceUpdateInput;
        })
    : [];
  return {
    id: listing?.id ?? 0,
    rowVersion: null,
    listPrice: listing?.listPrice ?? null,
    allInPrice: getAllInPriceFromListPriceForListing(listing, null) ?? null,
    netProceedsFloor: listing?.procsFloor ?? null,
    netProceedsCeiling: listing?.procsCeil ?? null,
    autoPricingEnabled: listing?.isAutoPrc ?? null,
    marketplacePriceUpdates,
    pricerSellerUserId: listing?.pricerId ?? EMPTY_GUID,
    buyerUserId: null,
    currencyCode: listing?.currency ?? null,
  };
};

export const usePricingForm = (
  listing: ListingDetails | null | undefined,
  onSubmitStart: () => void,
  onSubmitDone: (success?: boolean) => Promise<void>
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const { data: autoBroadcastSettings } =
    useAutoBroadcastSellerAccountSettings();
  const hasAutoBroadcastFeature = useUserHasFeature(
    Feature.AutoBroadcastWhenPricing
  );

  const methods = useForm<ListingDetailsPricingSectionUpdates>({
    defaultValues: listing?.dataIsLoaded[ListingDetailDataField.Pricing]
      ? getListingDetailPricingInput(listing)
      : undefined,
  });

  useEffect(() => {
    if (
      methods.formState.defaultValues == null &&
      listing?.dataIsLoaded[ListingDetailDataField.Pricing]
    ) {
      methods.reset(getListingDetailPricingInput(listing));
    }
  }, [methods, listing]);

  const onSubmitHandler = useCallback(
    async (pricingSectionForm: ListingDetailsPricingSectionUpdates) => {
      tryInvokeApi(
        async () => {
          onSubmitStart();
          const client = new ListingClient(activeAccountWebClientConfig);
          const result = await client.updateListingPricingSectionByListingId(
            pricingSectionForm.id,
            pricingSectionForm
          );

          if (isSuccess(result)) {
            console.log(
              '[AutoBroadcast] price update succeeded, attempting auto-broadcast'
            );
            try {
              await tryAutoBroadcastListing({
                client,
                listingId: pricingSectionForm.id,
                listingStatus: listing?.status,
                effectivePrice:
                  pricingSectionForm.listPrice ?? pricingSectionForm.allInPrice,
                autoBroadcastEnabled:
                  autoBroadcastSettings?.autoBroadcastManuallyPricedListings,
                hasFeature: hasAutoBroadcastFeature,
              });
            } catch {
              // Broadcast failures should not break the price update flow
            }
            // Reset the form to latest changed
            methods.reset(pricingSectionForm);
            await onSubmitDone();
          } else {
            showErrorDialog(
              'ListingClient.updateListingPricingSection',
              { message: result.message, status: result.status },
              {
                trackErrorData: {
                  pricingSectionForm,
                },
              }
            );
          }
        },
        (error) => {
          showErrorDialog('ListingClient.updateListingPricingSection', error, {
            trackErrorData: pricingSectionForm,
          });
        },
        () => {
          onSubmitDone(false);
        }
      );
    },
    [
      activeAccountWebClientConfig,
      autoBroadcastSettings,
      listing?.status,
      methods,
      onSubmitDone,
      onSubmitStart,
      showErrorDialog,
    ]
  );

  const onSubmit = useCallback(() => {
    methods.handleSubmit(onSubmitHandler)();
  }, [methods, onSubmitHandler]);

  const onReset = useCallback(() => {
    methods.reset(getListingDetailPricingInput(listing));
  }, [listing, methods]);

  const input = methods.watch();

  const hasChanges = useMemo(() => {
    const defaultValues = methods.formState.defaultValues;
    return (
      input.listPrice !== defaultValues?.listPrice ||
      input.allInPrice !== defaultValues?.allInPrice ||
      input.netProceedsFloor !== defaultValues?.netProceedsFloor ||
      input.netProceedsCeiling !== defaultValues?.netProceedsCeiling ||
      input.autoPricingEnabled !== defaultValues?.autoPricingEnabled ||
      input.pricerSellerUserId !== defaultValues?.pricerSellerUserId ||
      input.buyerUserId !== defaultValues?.buyerUserId ||
      input.currencyCode !== defaultValues?.currencyCode ||
      !isEqual(
        input.marketplacePriceUpdates,
        defaultValues?.marketplacePriceUpdates
      )
    );
  }, [input, methods.formState.defaultValues]);

  return {
    hasChanges,
    methods,
    onSubmit,
    onReset,
  };
};
